/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PageBreadcrumb } from '@repo/shared-client/components/blocks'
import {
  Alert,
  AlertDescription,
  AspectRatio,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Rating,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { FeatureFlag, getFeatureFlag, UserRole } from '@repo/shared-common'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import SFMediaImage from '@web/src/components/primatives/sf-media-image/sf-media-image'
import SFRichTextViewer from '@web/src/components/primatives/sf-rich-text-viewer/sf-rich-text-viewer'
import ChatMessageFloater from '@web/src/features/chat/chat-message-floater/chat-message-floater'
import MediaAdminEditDialog from '@web/src/features/media/media-admin-edit/media-admin-edit-dialog'
import MediaAdminEditMenuItems from '@web/src/features/media/media-admin-edit/media-admin-edit-menu-items'
import MediaUploaderDialog from '@web/src/features/media/media-uploader-dialog/media-uploader-dialog'
import SimpleMediaImageViewer from '@web/src/features/media/simple-media-image-viewer/simple-media-image-viewer'
import Reviews from '@web/src/features/review/reviews/reviews'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { AttachedMediaInfo, ProfileInfo, ProfileMediaInfo, VendorMarketItemInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { baseUrl } from '@web/src/lib/utilsClient'
import { EllipsisIcon, FilmIcon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  DeleteAttachedMediaMutation,
  DeleteAttachedMediaMutationVariables,
  DeleteVendorMarketItemMutation,
  DeleteVendorMarketItemMutationVariables,
  VendorMarketItemDetailQuery,
  VendorMarketItemDetailQueryVariables,
} from 'types/graphql'

/** Viewer cap for items when the listing owner is not supporter+ (permission id: vendor-market:item-image-limit); same value as member role in ROLE_FEATURE_FLAGS. */
const PUBLIC_VIEWER_SP_ITEM_IMAGE_LIMIT = getFeatureFlag(UserRole.MEMBER, FeatureFlag.VENDOR_MARKET_ITEM_IMAGE_LIMIT)

const SP_ITEM_QUERY: TypedDocumentNode<VendorMarketItemDetailQuery, VendorMarketItemDetailQueryVariables> = gql`
  query VendorMarketItemDetail($id: String!) {
    vendorMarketItem(id: $id) {
      ...VendorMarketItemInfo
      attachedMedia {
        ...AttachedMediaInfo
      }
      vendorMarket {
        id
        slug
        name
        ownerId
        owner {
          ...ProfileInfo
        }
      }
    }
  }
  ${VendorMarketItemInfo}
  ${AttachedMediaInfo}
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

const DELETE_IMAGE: TypedDocumentNode<DeleteAttachedMediaMutation, DeleteAttachedMediaMutationVariables> = gql`
  mutation DeleteAttachedMedia($id: String!) {
    deleteAttachedMedia(id: $id) {
      id
    }
  }
`

const MOVE_MEDIA_UP = gql`
  mutation MoveAttachedMediaUp($id: String!) {
    moveAttachedMediaUp(id: $id) {
      id
    }
  }
`

const MOVE_MEDIA_DOWN = gql`
  mutation MoveAttachedMediaDown($id: String!) {
    moveAttachedMediaDown(id: $id) {
      id
    }
  }
`

const MOVE_MEDIA_TO_TOP = gql`
  mutation MoveAttachedMediaToTop($id: String!) {
    moveAttachedMediaToTop(id: $id) {
      id
    }
  }
`

const MOVE_MEDIA_TO_BOTTOM = gql`
  mutation MoveAttachedMediaToBottom($id: String!) {
    moveAttachedMediaToBottom(id: $id) {
      id
    }
  }
`

const DELETE_ITEM: TypedDocumentNode<DeleteVendorMarketItemMutation, DeleteVendorMarketItemMutationVariables> = gql`
  mutation DeleteVendorMarketItem($id: String!) {
    deleteVendorMarketItem(id: $id) {
      id
    }
  }
`

const galleryThumbCardClass =
  'cursor-pointer border border-border bg-muted w-full transition-colors hover:border-primary hover:bg-muted/80'

type VendorMarketItemDetail = NonNullable<VendorMarketItemDetailQuery['vendorMarketItem']>
type VendorMarketItemAttachedMedia = NonNullable<VendorMarketItemDetail['attachedMedia']>[number]

export const Route = createFileRoute('/(auth)/(main-layout)/vendor-market/$vendorMarketIdOrSlug_/item/$itemId')({
  component: VendorMarketItemDetailPage,
  head: () => {
    return seo({
      title: `Item Details | ${env.VITE_APP_NAME}`,
      description: 'Vendor market item details',
      url: `${baseUrl()}/vendor-market`,
      robots: robotsMetaForEnvironment({ production: 'index, follow', staging: 'noindex,nofollow' }),
    })
  },
  loader: async ({ params, context }) => {
    const { queryRef } = await queryAndPreload(context, SP_ITEM_QUERY, {
      variables: { id: params.itemId },
    })
    return { queryRef }
  },
})

function VendorMarketItemDetailPage() {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const { queryRef } = Route.useLoaderData()
  const { data, error } = useReadQuery(queryRef as QueryRef<VendorMarketItemDetailQuery>)
  const navigate = useNavigate()
  const client = useApolloClient()

  const [uploadImageModal, setUploadImageModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)

  const item = data?.vendorMarketItem
  const vendorRouteSegment =
    item?.vendorMarket != null ? (item.vendorMarket.slug ?? item.vendorMarket.id) : (item?.vendorMarketId ?? '')

  const handleRefetch = async () => {
    await client.refetchQueries({ include: [SP_ITEM_QUERY] })
  }

  const [deleteItem] = useMutation(DELETE_ITEM, {
    onCompleted: () => {
      toast.success(t('Item Deleted'))
      navigate({
        to: '/vendor-market/$vendorMarketIdOrSlug',
        params: { vendorMarketIdOrSlug: vendorRouteSegment } as never,
      })
    },
    onError: (err) => toast.error(err.message),
  })

  if (error) return <ErrorAlert error={error} />
  if (!item) return <ErrorAlert error={new Error(t('Item not found'))} />

  const hasMedia = item.attachedMedia && item.attachedMedia.length > 0
  let avgRating = 0
  if (item.reviewCount > 0) {
    avgRating = Math.min(Math.max(0, item.reviewSum / item.reviewCount), 5.0)
  }

  const priceDisplay = () => {
    if (item.contactForPricing) return t('Contact for Pricing')
    if (item.price != null && item.priceMax != null) return `$${item.price.toFixed(2)} - $${item.priceMax.toFixed(2)}`
    if (item.price != null) return `$${item.price.toFixed(2)}`
    return null
  }

  const isListingOwner = currentUser?.profile?.id === item.vendorMarket?.ownerId
  const showNonSupporterItemMediaLimitNotice =
    isListingOwner &&
    item.vendorMarket?.owner &&
    !item.vendorMarket.owner.supporter &&
    (item.attachedMedia?.length ?? 0) > PUBLIC_VIEWER_SP_ITEM_IMAGE_LIMIT

  return (
    <div className="mx-auto w-full max-w-4xl px-2 py-4 md:px-4">
      <div className="flex w-full flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t('Home'), to: '/' },
            { label: t('Categories'), to: '/vendor-market' },
            { label: t('Discover Vendors'), to: '/vendor-market/list' },
            {
              label: item.vendorMarket?.name ?? '',
              render: (
                <Link
                  params={{ vendorMarketIdOrSlug: vendorRouteSegment } as never}
                  to="/vendor-market/$vendorMarketIdOrSlug"
                />
              ),
            },
            { label: item.name },
          ]}
          separatorGap={2}
        />

        <div className="flex w-full items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
            <h1 className="font-bold text-2xl">{item.name}</h1>
            <div className="flex w-full items-center justify-between gap-2">
              {priceDisplay() ? <span className="text-lg text-muted-foreground">{priceDisplay()}</span> : null}
              <div className="flex items-center gap-2">
                <Rating size="sm" value={avgRating} variant="default" />
                <span className="pr-2 text-sm">({item.reviewCount})</span>
              </div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger render={<Button aria-label={t('Item options')} size="icon" type="button" />}>
              <EllipsisIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {item.canEdit ? (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: '/vendor-market/$vendorMarketIdOrSlug/item/$itemId/edit',
                      params: { vendorMarketIdOrSlug: vendorRouteSegment, itemId: item.id } as never,
                    })
                  }
                >
                  {t('Edit')}
                </DropdownMenuItem>
              ) : null}
              {item.canDelete ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => deleteItem({ variables: { id: item.id } })} variant="destructive">
                    {t('Delete')}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex w-full flex-col items-start gap-4">
          {hasMedia ? (
            <div className="flex w-full flex-col items-stretch gap-2 md:flex-row">
              {item.attachedMedia.map((attachedMedia, idx) => {
                if (idx > 1) {
                  return null
                }

                const showHeroPairOnMd = item.attachedMedia.length > 1

                return (
                  <Card
                    className={cn(
                      galleryThumbCardClass,
                      'overflow-hidden p-0',
                      showHeroPairOnMd && 'md:w-1/2 md:min-w-0',
                      idx > 0 && 'hidden md:block'
                    )}
                    key={attachedMedia.id}
                    onClick={() => {
                      setImageIndex(idx)
                      setViewModal(true)
                    }}
                  >
                    <AspectRatio ratio={16 / 9}>
                      <SFMediaImage media={attachedMedia} />
                    </AspectRatio>
                  </Card>
                )
              })}
            </div>
          ) : null}

          <div className="flex w-full items-start justify-start gap-2 overflow-x-auto overflow-y-hidden">
            {item.canUploadMedia ? (
              <AspectRatio className="w-[125px] min-w-[125px]" ratio={1}>
                <Card
                  className={cn(galleryThumbCardClass, 'flex h-full flex-col items-center justify-center gap-2 p-2')}
                  onClick={() => setUploadImageModal(true)}
                >
                  <FilmIcon className="size-8" />
                  <div className="flex flex-col text-center text-base">
                    <span>{t('Upload')}</span>
                    <span>{t('Media')}</span>
                  </div>
                </Card>
              </AspectRatio>
            ) : null}

            {item.attachedMedia?.map((attachedMedia, idx) => (
              <ItemImageCard
                canDeleteMedia={item.canDeleteMedia}
                idx={idx}
                key={attachedMedia.id}
                media={attachedMedia}
                onImageSelected={(selectedIdx) => {
                  setImageIndex(selectedIdx)
                  setViewModal(true)
                }}
                onRefetch={handleRefetch}
                totalMedia={item.attachedMedia.length}
              />
            ))}
          </div>

          {showNonSupporterItemMediaLimitNotice ? (
            <Alert variant="destructive">
              <AlertDescription className="flex flex-col items-start gap-2">
                <span>
                  {t(
                    'Only one media file per item is visible to visitors using a free account. Become a supporter to allow visitors to see all media on your items.'
                  )}
                </span>
                <Button className="w-fit" nativeButton={false} render={<Link to="/supporter" />}>
                  {t('Become a Supporter')}
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}
        </div>

        {item.description ? (
          <Card className="w-full">
            <CardContent>
              <SFRichTextViewer content={item.description} />
            </CardContent>
          </Card>
        ) : null}

        <Reviews
          canReview={item.canReview}
          vendorMarketIdOrSlug={vendorRouteSegment}
          vendorMarketItemIdOrSlug={item.id}
          vendorMarketOwnerId={item.vendorMarket?.ownerId ?? undefined}
        />
      </div>

      <MediaUploaderDialog
        onOpenChange={(e) => setUploadImageModal(e.open)}
        onUploadComplete={handleRefetch}
        open={uploadImageModal}
        vendorMarketItemId={item.id}
      />

      {hasMedia ? (
        <SimpleMediaImageViewer
          attachedMedia={true}
          imageIndex={imageIndex}
          mediaArray={item.attachedMedia}
          onMediaChange={handleRefetch}
          onOpenChange={(e) => setViewModal(e.open)}
          open={viewModal}
          vendorMarketId={item.vendorMarketId}
        />
      ) : null}
      {currentUser.profile?.id !== item.vendorMarket?.ownerId ? (
        <ChatMessageFloater profileId={item.vendorMarket?.ownerId} />
      ) : null}
    </div>
  )
}

type ItemImageCardProps = {
  idx: number
  media: VendorMarketItemAttachedMedia
  onImageSelected: (idx: number) => void
  onRefetch: () => void | Promise<void>
  totalMedia: number
  canDeleteMedia: boolean
}

function ItemImageCard({ onRefetch, media, idx, onImageSelected, totalMedia, canDeleteMedia }: ItemImageCardProps) {
  const { t } = useTranslation()
  const [deleteModal, setDeleteModal] = useState(false)
  const [adminEditModal, setAdminEditModal] = useState(false)

  const [deleteImage] = useMutation(DELETE_IMAGE, {
    onCompleted: () => {
      toast.success(t('Image Deleted'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveUp] = useMutation(MOVE_MEDIA_UP, {
    onCompleted: () => {
      toast.success(t('Media moved up'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveDown] = useMutation(MOVE_MEDIA_DOWN, {
    onCompleted: () => {
      toast.success(t('Media moved down'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveToTop] = useMutation(MOVE_MEDIA_TO_TOP, {
    onCompleted: () => {
      toast.success(t('Media moved to top'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveToBottom] = useMutation(MOVE_MEDIA_TO_BOTTOM, {
    onCompleted: () => {
      toast.success(t('Media moved to bottom'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleDeleteImage = () => {
    setDeleteModal(false)
    deleteImage({ variables: { id: media.id } })
  }

  return (
    <div className="relative shrink-0">
      <AspectRatio className="relative w-[125px] min-w-[125px]" ratio={1}>
        <Card className={cn(galleryThumbCardClass, 'h-full overflow-hidden p-0')} onClick={() => onImageSelected(idx)}>
          <SFMediaImage media={media} />
        </Card>
      </AspectRatio>
      {canDeleteMedia ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="absolute top-0.5 right-0.5 z-10"
            onClick={(e) => e.stopPropagation()}
            render={
              <Button
                aria-label={t('Media options')}
                className="rounded-full border-black"
                size="icon-xs"
                type="button"
                variant="default"
              />
            }
          >
            <EllipsisIcon />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
            {idx > 0 ? (
              <DropdownMenuItem onClick={() => moveToTop({ variables: { id: media.id } })}>
                {t('Move to Top')}
              </DropdownMenuItem>
            ) : null}
            {idx > 0 ? (
              <DropdownMenuItem onClick={() => moveUp({ variables: { id: media.id } })}>
                {t('Move Up')}
              </DropdownMenuItem>
            ) : null}
            {idx < totalMedia - 1 ? (
              <DropdownMenuItem onClick={() => moveDown({ variables: { id: media.id } })}>
                {t('Move Down')}
              </DropdownMenuItem>
            ) : null}
            {idx < totalMedia - 1 ? (
              <DropdownMenuItem onClick={() => moveToBottom({ variables: { id: media.id } })}>
                {t('Move to Bottom')}
              </DropdownMenuItem>
            ) : null}
            <MediaAdminEditMenuItems mediaId={media.id} onOpen={() => setAdminEditModal(true)} />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setDeleteModal(true)} variant="destructive">
              {t('Delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <ConfirmDialog
        onConfirm={handleDeleteImage}
        onOpenChange={(e) => setDeleteModal(e.open)}
        open={deleteModal}
        text={t('Are you sure you want to delete this image?')}
        title={t('Are you sure?')}
        variant="delete"
      />
      <MediaAdminEditDialog
        isAttached
        mediaId={media.id}
        onMediaChange={onRefetch}
        onOpenChange={(e) => setAdminEditModal(e.open)}
        open={adminEditModal}
      />
    </div>
  )
}
